import React, { createContext, useState, useContext, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Prefer session-based auth. If session empty but legacy local exists, migrate once
    const sessionAuth = sessionStorage.getItem('adminAuth');
    const localAuth = !sessionAuth ? localStorage.getItem('adminAuth') : null;

    try {
      const source = sessionAuth || localAuth;
      if (source) {
        const authData = JSON.parse(source);
        setIsAuthenticated(true);
        setAdminUser(authData.user);
        // Migrate legacy localStorage to sessionStorage and clear local
        if (!sessionAuth) {
          sessionStorage.setItem('adminAuth', source);
          localStorage.removeItem('adminAuth');
        }
      }
    } catch (e) {
      // Corrupt storage; clear both
      sessionStorage.removeItem('adminAuth');
      localStorage.removeItem('adminAuth');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    try {
      // Auto-detect API URL
      const isLocalhost = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1';
      const API_URL = process.env.REACT_APP_API_URL || 
                      (isLocalhost 
                        ? 'http://localhost:5000/api' 
                        : '/backend-php/api');
      
      const response = await fetch(`${API_URL}/admin/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsAuthenticated(true);
        setAdminUser(data.user);
        // Store only in sessionStorage so closing the browser logs out
        sessionStorage.setItem('adminAuth', JSON.stringify({ user: data.user }));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Invalid credentials' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Unable to connect to server. Please try again.' };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setAdminUser(null);
    sessionStorage.removeItem('adminAuth');
    localStorage.removeItem('adminAuth');
  };

  return (
    <AdminContext.Provider value={{ isAuthenticated, adminUser, login, logout, isLoading }}>
      {children}
    </AdminContext.Provider>
  );
};

